import type { LogicalType } from '@/types'
import type { DataTypeDefinition, DialectAdapter, DialectWarning } from '../dialectTypes'

const DATA_TYPES: DataTypeDefinition[] = [
  { value: 'SMALLINT',        label: 'SMALLINT',        logicalType: 'smallint' },
  { value: 'INTEGER',         label: 'INTEGER',         logicalType: 'integer' },
  { value: 'BIGINT',          label: 'BIGINT',          logicalType: 'bigint' },
  { value: 'NUMERIC(10,2)',   label: 'NUMERIC(p,s)',    logicalType: 'decimal', hasPrecisionScale: true },
  { value: 'DECIMAL(10,2)',   label: 'DECIMAL(p,s)',    logicalType: 'decimal', hasPrecisionScale: true },
  { value: 'REAL',            label: 'REAL',            logicalType: 'float' },
  { value: 'DOUBLE PRECISION',label: 'DOUBLE PRECISION',logicalType: 'float' },
  { value: 'VARCHAR(255)',    label: 'VARCHAR(n)',       logicalType: 'string', hasLength: true },
  { value: 'CHAR(1)',         label: 'CHAR(n)',          logicalType: 'string', hasLength: true },
  { value: 'TEXT',            label: 'TEXT',             logicalType: 'text' },
  { value: 'BOOLEAN',         label: 'BOOLEAN',          logicalType: 'boolean' },
  { value: 'DATE',            label: 'DATE',             logicalType: 'date' },
  { value: 'TIME',            label: 'TIME',             logicalType: 'time' },
  { value: 'TIMESTAMP',       label: 'TIMESTAMP',        logicalType: 'datetime' },
  { value: 'TIMESTAMPTZ',     label: 'TIMESTAMPTZ',      logicalType: 'datetime' },
  { value: 'UUID',            label: 'UUID',             logicalType: 'uuid' },
  { value: 'JSON',            label: 'JSON',             logicalType: 'json' },
  { value: 'JSONB',           label: 'JSONB',            logicalType: 'json' },
  { value: 'BYTEA',           label: 'BYTEA',            logicalType: 'binary' },
  { value: 'SERIAL',          label: 'SERIAL',           logicalType: 'integer' },
  { value: 'BIGSERIAL',       label: 'BIGSERIAL',        logicalType: 'bigint' },
]

const LOGICAL_TO_PHYSICAL: Record<LogicalType, string> = {
  string:   'VARCHAR(255)',
  text:     'TEXT',
  integer:  'INTEGER',
  bigint:   'BIGINT',
  smallint: 'SMALLINT',
  decimal:  'NUMERIC(10,2)',
  float:    'DOUBLE PRECISION',
  boolean:  'BOOLEAN',
  date:     'DATE',
  time:     'TIME',
  datetime: 'TIMESTAMPTZ',
  uuid:     'UUID',
  json:     'JSONB',
  binary:   'BYTEA',
}

function normalizeTypeKey(type: string): string {
  return type.trim().toUpperCase().replace(/\s*\(.*\)/, '').replace(/\s+/g, ' ')
}

export const postgresqlDialect: DialectAdapter = {
  id: 'postgresql',
  label: 'PostgreSQL',
  dataTypes: DATA_TYPES,

  mapLogicalToPhysical(type) {
    return LOGICAL_TO_PHYSICAL[type] ?? 'TEXT'
  },

  mapPhysicalToLogical(type) {
    const key = normalizeTypeKey(type)
    switch (key) {
      case 'SMALLINT':
      case 'INT2':
        return 'smallint'
      case 'INTEGER':
      case 'INT':
      case 'INT4':
      case 'SERIAL':
        return 'integer'
      case 'BIGINT':
      case 'INT8':
      case 'BIGSERIAL':
        return 'bigint'
      case 'NUMERIC':
      case 'DECIMAL':
        return 'decimal'
      case 'REAL':
      case 'FLOAT4':
      case 'DOUBLE PRECISION':
      case 'FLOAT8':
        return 'float'
      case 'VARCHAR':
      case 'CHARACTER VARYING':
      case 'CHAR':
      case 'CHARACTER':
        return 'string'
      case 'TEXT':
        return 'text'
      case 'BOOLEAN':
      case 'BOOL':
        return 'boolean'
      case 'DATE':
        return 'date'
      case 'TIME':
      case 'TIMETZ':
        return 'time'
      case 'TIMESTAMP':
      case 'TIMESTAMPTZ':
        return 'datetime'
      case 'UUID':
        return 'uuid'
      case 'JSON':
      case 'JSONB':
        return 'json'
      case 'BYTEA':
        return 'binary'
      default:
        return 'text'
    }
  },

  validateColumn(column) {
    const warnings: DialectWarning[] = []
    const upper = column.type.trim().toUpperCase()
    if (upper === 'NVARCHAR' || upper.startsWith('NVARCHAR(')) {
      warnings.push({ column: column.name, message: 'NVARCHAR is a SQL Server type; use VARCHAR or TEXT in PostgreSQL.', severity: 'warning' })
    }
    return warnings
  },
}
